/**
 * =============================================================================
 * useMetricCard
 * =============================================================================
 * React hook that drives the full MetricCard data pipeline:
 *
 *   1. Apply context tokens ({{today}}, {{vendorId}}, etc.) to the instance
 *   2. Fire all declared apiCalls in parallel via ReparoApi
 *   3. Run resolveExtractions  — Phase 1: named values from responses
 *   4. Run resolveFieldBindings — Phase 2: card slot values from named extractions
 *   5. Return { fields, isLoading, error }
 *
 * MetricCard.tsx calls this hook and renders from the returned fields.
 * No fetching or computation logic lives in the component itself.
 *
 * The pipeline runs through React Query so a card's result is reused across mounts. Two of
 * the Home cards are full Inventory computations on the backend (the Snapshot for Blended
 * Days on Hand, the Planning grid for In-Transit) that cost 1.5-2.5 s each and were re-run
 * on every visit to the page, Home -> Inventory -> Home included. Within
 * METRIC_CARD_STALE_MS a revisit renders the previous result and fires nothing; after it,
 * the next mount refetches as before. The key carries the organisation so a consultant
 * switching brand in the same session never sees another brand's number.
 */

import type { MetricCardInstance } from '@diagon-alley/common';
import { useQuery } from '@tanstack/react-query';
import { ReparoApi } from '../api';
import {
    type ResolvedMetricCardFields,
    applyContextTokens,
    resolveExtractions,
    resolveFieldBindings,
} from '../utils/compute-engine';

const EMPTY_FIELDS: ResolvedMetricCardFields = {
    label: null,
    value: null,
    trend: null,
    subtext: null,
};

/** How long a card's resolved fields are reused across mounts before being refetched. */
export const METRIC_CARD_STALE_MS = 5 * 60 * 1000;

/** The organisation the API will answer for — it comes from the stored user, not the call. */
const readOrganizationId = (): string => {
    try {
        const userJson = localStorage.getItem('user');
        if (!userJson) return '';
        const user = JSON.parse(userJson) as { organizationId?: string };
        return user.organizationId ?? '';
    } catch {
        return '';
    }
};

/** Steps 1-4 of the pipeline, unchanged in substance from the pre-cache implementation. */
export const resolveMetricCardFields = async (
    instance: MetricCardInstance,
    context?: Record<string, string>,
): Promise<ResolvedMetricCardFields> => {
    // ── Step 1: Resolve context tokens ──────────────────────────────────────
    const resolved = applyContextTokens(instance, context);

    // ── Step 2: Fire all API calls in parallel ──────────────────────────────
    const callResults = await Promise.all(
        resolved.apiCalls.map(async (call) => {
            let url = call.endpoint;

            // For GET requests with params, append as query string
            if (call.method === 'GET' && call.params) {
                const qs = new URLSearchParams();
                for (const [k, v] of Object.entries(call.params)) {
                    if (v !== null && v !== undefined) {
                        qs.append(k, String(v));
                    }
                }
                const qsStr = qs.toString();
                if (qsStr) url = `${url}?${qsStr}`;
            }

            const data = await ReparoApi({
                method: call.method,
                url,
                body: call.method === 'POST' && call.params ? JSON.stringify(call.params) : null,
            });

            return { id: call.id, data };
        }),
    );

    const responseMap = new Map(callResults.map((r) => [r.id, r.data]));

    // ── Step 3: Resolve extractions ─────────────────────────────────────────
    const extractionMap = resolveExtractions(resolved.extractions, responseMap);

    // ── Step 4: Resolve field bindings ──────────────────────────────────────
    return resolveFieldBindings(resolved.fieldBindings, extractionMap);
};

export const useMetricCard = (
    instance: MetricCardInstance,
    context?: Record<string, string>,
): { fields: ResolvedMetricCardFields; isLoading: boolean; error: string | null } => {
    // Stable reference check — only re-run if instance id or context values change
    const instanceId = instance.id;
    const contextKey = context ? JSON.stringify(context) : '';
    const organizationId = readOrganizationId();

    const { data, isLoading, error } = useQuery({
        queryKey: ['metric-card', organizationId, instanceId, contextKey],
        queryFn: () => resolveMetricCardFields(instance, context),
        staleTime: METRIC_CARD_STALE_MS,
        cacheTime: 2 * METRIC_CARD_STALE_MS,
    });

    return {
        fields: data ?? EMPTY_FIELDS,
        isLoading,
        error: error ? (error instanceof Error ? error.message : 'Failed to load metric') : null,
    };
};
